// src/components/PerformanceAnalyzer.js
import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  LinearProgress,
} from '@mui/material';
import SpeedIcon from '@mui/icons-material/Speed';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { Section } from './ui/Section';
import { FeatureCard } from './ui/FeatureCard';
import { StatsCard } from './ui/StatsCard';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { ProgressBar } from './ui/ProgressBar';
import { AnimatedBackground } from './ui/AnimatedBackground';
import { Download } from '@mui/icons-material';

const PerformanceAnalyzer = () => {
  const [url, setUrl] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const isValidUrl = (string) => {
    try {
      const newUrl = new URL(string);
      return newUrl.protocol === 'http:' || newUrl.protocol === 'https:';
    } catch (_) {
      return false;
    }
  };

  const generatePDF = () => {
    if (!results) return;

    // Create PDF content
    const pdfContent = `
Performance Analysis Report
Generated by Zolang Nexora Performance Analyzer
================================================

Website: ${results.url}
Domain: ${results.domain}
Analysis Date: ${results.timestamp}

OVERALL SCORE: ${results.overallScore}/100 (${getScoreLabel(results.overallScore)})

CORE WEB VITALS:
----------------
Largest Contentful Paint (LCP): ${results.coreWebVitals.lcp.toFixed(2)}s - ${getVitalStatus('lcp', results.coreWebVitals.lcp).status}
First Input Delay (FID): ${results.coreWebVitals.fid.toFixed(0)}ms - ${getVitalStatus('fid', results.coreWebVitals.fid).status}
Cumulative Layout Shift (CLS): ${results.coreWebVitals.cls.toFixed(3)} - ${getVitalStatus('cls', results.coreWebVitals.cls).status}

PERFORMANCE METRICS:
-------------------
First Contentful Paint: ${results.performanceMetrics.firstContentfulPaint.toFixed(2)}s
Time to Interactive: ${results.performanceMetrics.timeToInteractive.toFixed(2)}s
Speed Index: ${results.performanceMetrics.speedIndex.toFixed(2)}s
Total Blocking Time: ${results.performanceMetrics.totalBlockingTime.toFixed(0)}ms

ADDITIONAL SCORES:
-----------------
SEO Score: ${results.seoScore}/100
Accessibility Score: ${results.accessibilityScore}/100
Best Practices Score: ${results.bestPracticesScore}/100

OPTIMIZATION OPPORTUNITIES:
--------------------------
${results.opportunities.map((opp, index) => `${index + 1}. ${opp}`).join('\n')}

PASSED AUDITS:
-------------
${results.passedAudits.map((audit, index) => `${index + 1}. ${audit}`).join('\n')}

RECOMMENDATIONS:
---------------
1. Address the optimization opportunities listed above
2. Monitor Core Web Vitals regularly
3. Implement suggested improvements for better user experience
4. Consider CDN and caching strategies

Report generated by Zolang Nexora Performance Analyzer
https://zolang-nexora.com
    `;

    // Create and download PDF file
    const blob = new Blob([pdfContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `performance-report-${results.domain}-${new Date().getTime()}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const analyzePerformance = async () => {
    if (!url.trim()) {
      setError('Please enter a URL');
      return;
    }

    if (!isValidUrl(url)) {
      setError('Please enter a valid URL (include http:// or https://)');
      return;
    }

    setError('');
    setAnalyzing(true);
    setResults(null);

    try {
      // Simulate API call with realistic performance data
      setTimeout(() => {
        const domain = new URL(url).hostname;
        
        // Generate realistic performance data based on domain
        const isFastSite = domain.includes('google') || domain.includes('cloudflare');
        const isSlowSite = domain.includes('wordpress') || domain.includes('shared');
        
        const mockResults = {
          url: url,
          domain: domain,
          overallScore: isFastSite ? 92 : isSlowSite ? 65 : Math.floor(Math.random() * 30) + 70,
          timestamp: new Date().toLocaleString(),
          coreWebVitals: {
            lcp: isFastSite ? (Math.random() * 1 + 1.5) : isSlowSite ? (Math.random() * 2 + 4) : (Math.random() * 2 + 2), // 1.5-6 seconds
            fid: isFastSite ? (Math.random() * 50 + 50) : isSlowSite ? (Math.random() * 150 + 150) : (Math.random() * 100 + 50), // 50-300ms
            cls: isFastSite ? (Math.random() * 0.05) : isSlowSite ? (Math.random() * 0.2 + 0.1) : (Math.random() * 0.15), // 0-0.3
          },
          performanceMetrics: {
            firstContentfulPaint: isFastSite ? (Math.random() * 0.5 + 1) : isSlowSite ? (Math.random() * 1 + 2) : (Math.random() * 1 + 1.5),
            timeToInteractive: isFastSite ? (Math.random() * 1 + 2) : isSlowSite ? (Math.random() * 2 + 4) : (Math.random() * 1.5 + 2.5),
            speedIndex: isFastSite ? (Math.random() * 1 + 2) : isSlowSite ? (Math.random() * 2 + 4) : (Math.random() * 1.5 + 2.5),
            totalBlockingTime: isFastSite ? (Math.random() * 100 + 50) : isSlowSite ? (Math.random() * 300 + 200) : (Math.random() * 200 + 100),
          },
          opportunities: generateOpportunities(isFastSite),
          passedAudits: generatePassedAudits(isFastSite),
          seoScore: isFastSite ? 95 : isSlowSite ? 70 : Math.floor(Math.random() * 25) + 75,
          accessibilityScore: isFastSite ? 90 : isSlowSite ? 65 : Math.floor(Math.random() * 25) + 70,
          bestPracticesScore: isFastSite ? 88 : isSlowSite ? 72 : Math.floor(Math.random() * 20) + 75,
        };
        
        setResults(mockResults);
        setAnalyzing(false);
      }, 3000);

    } catch (err) {
      setError('Failed to analyze the website. Please try again.');
      setAnalyzing(false);
    }
  };

  const generateOpportunities = (isFastSite) => {
    const allOpportunities = [
      'Reduce unused JavaScript (save ~150KB)',
      'Optimize images (save ~200KB)',
      'Defer offscreen images',
      'Minify CSS (save ~50KB)',
      'Enable text compression (save ~300KB)',
      'Remove unused CSS',
      'Use efficient cache policy',
      'Reduce server response time',
      'Avoid multiple page redirects',
    ];

    return isFastSite 
      ? allOpportunities.slice(0, 2) 
      : allOpportunities.slice(0, 5);
  };

  const generatePassedAudits = (isFastSite) => {
    const allAudits = [
      'Uses HTTPS',
      'Has a meta description',
      'Has a valid robots.txt',
      'Document uses legible font sizes',
      'Links have descriptive text',
      'Image elements have [alt] attributes',
      'Page has a <title> element',
      'Properly sizes images',
      'Avoids deprecated APIs',
    ];

    return isFastSite 
      ? allAudits 
      : allAudits.slice(0, 6);
  };

  const getScoreColor = (score) => {
    if (score >= 90) return '#4caf50';
    if (score >= 50) return '#ff9800';
    return '#f44336';
  };

  const getScoreLabel = (score) => {
    if (score >= 90) return 'Excellent';
    if (score >= 80) return 'Good';
    if (score >= 60) return 'Needs Improvement';
    return 'Poor';
  };

  const getVitalStatus = (metric, value) => {
    const thresholds = {
      lcp: { good: 2.5, poor: 4 },
      fid: { good: 100, poor: 300 },
      cls: { good: 0.1, poor: 0.25 },
      firstContentfulPaint: { good: 1.8, poor: 3 },
      timeToInteractive: { good: 3.8, poor: 7.3 },
      speedIndex: { good: 3.4, poor: 5.8 },
    };

    const threshold = thresholds[metric];
    if (!threshold) return { color: '#9e9e9e', status: 'Unknown' };

    if (value <= threshold.good) return { color: '#4caf50', status: 'Good' };
    if (value <= threshold.poor) return { color: '#ff9800', status: 'Needs Improvement' };
    return { color: '#f44336', status: 'Poor' };
  };

  const formatValue = (metric, value) => {
    if (metric.includes('Time') || metric === 'fid') return `${value.toFixed(0)}ms`;
    if (typeof value === 'number' && value < 1) return value.toFixed(3);
    if (typeof value === 'number') return value.toFixed(2) + 's';
    return value;
  };

  return (
    <>
      <AnimatedBackground />
      
      {/* Header Section */}
      <Section background="gradient" spacing={6}>
        <br/><br/><br/>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Chip 
              label="Performance Tool" 
              sx={{ 
                background: 'linear-gradient(45deg, #8b0000, #ff4d4d)',
                color: 'white',
                mb: 2,
                fontWeight: 'bold',
              }}
            />
            <Typography 
              variant="h1" 
              gutterBottom
              sx={{ 
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                mb: 2,
              }}
            >
              Website Performance Analyzer
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.8)',
                mb: 4,
              }}
            >
              Get comprehensive performance insights for any website
            </Typography>
          </Box>
{/* Features Grid */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <FeatureCard
                icon={<SpeedIcon />}
                title="Core Web Vitals"
                description="LCP, FID, CLS analysis"
                gradient
                sx={{ height: '100%' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FeatureCard
                icon={<AnalyticsIcon />}
                title="Performance Metrics"
                description="Load times & speed index"
                sx={{ height: '100%' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FeatureCard
                icon={<TrendingUpIcon />}
                title="SEO Analysis"
                description="Search engine optimization"
                sx={{ height: '100%' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FeatureCard
                icon={<SearchIcon />}
                title="Best Practices"
                description="Web standards compliance"
                sx={{ height: '100%' }}
              />
            </Grid>
          </Grid>
          {/* URL Input */}
          <Card sx={{ 
            background: 'linear-gradient(135deg, rgba(26, 31, 61, 0.8) 0%, rgba(10, 17, 40, 0.9) 100%)',
            border: '1px solid rgba(139, 0, 0, 0.3)',
            mb: 4,
          }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: '#ff4d4d' }}>
                Analyze Website Performance
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Input
                  placeholder="https://example.com"
                  value={url}
                  onChange={(e) => {
                    setUrl(e.target.value);
                    setError('');
                  }}
                  fullWidth
                  error={!!error}
                  helperText={error}
                />
                <Button
                  variant="contained"
                  onClick={analyzePerformance}
                  loading={analyzing}
                  startIcon={<SpeedIcon />}
                  sx={{ minWidth: 140, height: 56 }}
                >
                  Analyze
                </Button>
              </Box>
              
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mt: 1 }}>
                Enter a complete URL including http:// or https://
              </Typography>
            </CardContent>
          </Card>
        </Container>
      </Section>

      {/* Loading State */}
      {analyzing && (
        <Section spacing={4}>
          <Container>
            <Card sx={{ 
              background: 'linear-gradient(135deg, rgba(26, 31, 61, 0.8) 0%, rgba(10, 17, 40, 0.9) 100%)',
              border: '1px solid rgba(139, 0, 0, 0.3)',
            }}>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <SpeedIcon sx={{ fontSize: 60, color: '#ff4d4d', mb: 3 }} />
                <Typography variant="h4" gutterBottom sx={{ color: 'white' }}>
                  Analyzing Website Performance
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 4 }}>
                  Testing {url}...
                </Typography>
                <LinearProgress 
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    background: 'rgba(139, 0, 0, 0.2)',
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(45deg, #8b0000, #ff4d4d)',
                      borderRadius: 4,
                    }
                  }} 
                />
              </CardContent>
            </Card>
          </Container>
        </Section>
      )}

      {/* Results Section */}
      {results && (
        <Section spacing={4}>
          <Container>
            {/* Overview Card */}
            <Card sx={{ 
              background: 'linear-gradient(135deg, rgba(26, 31, 61, 0.8) 0%, rgba(10, 17, 40, 0.9) 100%)',
              border: '1px solid rgba(139, 0, 0, 0.3)',
              mb: 4,
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                  <Box>
                    <Typography variant="h4" sx={{ color: 'white', mb: 1 }}>
                      {results.domain}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                      Analyzed on {results.timestamp}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography 
                      variant="h2" 
                      sx={{ 
                        color: getScoreColor(results.overallScore),
                        fontWeight: 'bold',
                        lineHeight: 1,
                      }}
                    >
                      {results.overallScore}
                    </Typography>
                    <Chip 
                      label={getScoreLabel(results.overallScore)}
                      sx={{ 
                        background: getScoreColor(results.overallScore),
                        color: 'white',
                        fontWeight: 'bold',
                      }}
                    />
                  </Box>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <StatsCard 
                      number={results.seoScore} 
                      label="SEO Score" 
                      animate 
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <StatsCard 
                      number={results.accessibilityScore} 
                      label="Accessibility" 
                      animate 
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <StatsCard 
                      number={results.bestPracticesScore} 
                      label="Best Practices" 
                      animate 
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Grid container spacing={3}>
              {/* Core Web Vitals */}
              <Grid item xs={12} lg={6}>
                <Card sx={{ 
                  background: 'linear-gradient(135deg, rgba(26, 31, 61, 0.8) 0%, rgba(10, 17, 40, 0.9) 100%)',
                  border: '1px solid rgba(139, 0, 0, 0.3)',
                  height: '100%',
                }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom sx={{ color: '#ff4d4d', mb: 3 }}>
                      Core Web Vitals
                    </Typography>
                    
                    {Object.entries(results.coreWebVitals).map(([metric, value]) => {
                      const status = getVitalStatus(metric, value);
                      return (
                        <Box key={metric} sx={{ mb: 3 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="body1" sx={{ color: 'white', textTransform: 'uppercase', fontWeight: 600 }}>
                              {metric}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body1" sx={{ color: status.color, fontWeight: 'bold' }}>
                                {formatValue(metric, value)}
                              </Typography>
                              <Chip 
                                label={status.status} 
                                size="small"
                                sx={{ 
                                  background: status.color,
                                  color: 'white',
                                  fontWeight: 'bold',
                                }}
                              />
                            </Box>
                          </Box>
                          <ProgressBar 
                            value={Math.min((value / (metric === 'cls' ? 0.3 : metric === 'fid' ? 500 : 6)) * 100, 100)} 
                            label=""
                            color={status.color}
                          />
                        </Box>
                      );
                    })}
                  </CardContent>
                </Card>
              </Grid>

              {/* Performance Metrics */}
              <Grid item xs={12} lg={6}>
                <Card sx={{ 
                  background: 'linear-gradient(135deg, rgba(26, 31, 61, 0.8) 0%, rgba(10, 17, 40, 0.9) 100%)',
                  border: '1px solid rgba(139, 0, 0, 0.3)',
                  height: '100%',
                }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom sx={{ color: '#ff4d4d', mb: 3 }}>
                      Performance Metrics
                    </Typography>
                    
                    {Object.entries(results.performanceMetrics).map(([metric, value]) => {
                      const status = getVitalStatus(metric, value);
                      return (
                        <Box key={metric} sx={{ mb: 2.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', textTransform: 'capitalize' }}>
                              {metric.replace(/([A-Z])/g, ' $1').trim()}
                            </Typography>
                            <Typography variant="body2" sx={{ color: status.color, fontWeight: 'bold', fontFamily: 'monospace' }}>
                              {formatValue(metric, value)}
                            </Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={Math.min((value / (metric.includes('Time') ? 600 : 8)) * 100, 100)} 
                            sx={{ 
                              height: 6, 
                              borderRadius: 3,
                              background: 'rgba(139, 0, 0, 0.2)',
                              '& .MuiLinearProgress-bar': {
                                background: status.color,
                                borderRadius: 3,
                              }
                            }} 
                          />
                        </Box>
                      );
                    })}
                  </CardContent>
                </Card>
              </Grid>

              {/* Opportunities */}
              <Grid item xs={12} md={6}>
                <Card sx={{ 
                  background: 'linear-gradient(135deg, rgba(26, 31, 61, 0.8) 0%, rgba(10, 17, 40, 0.9) 100%)',
                  border: '1px solid rgba(139, 0, 0, 0.3)',
                  height: '100%',
                }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom sx={{ color: '#ff4d4d', mb: 3 }}>
                      Optimization Opportunities
                    </Typography>
                    
                    {results.opportunities.map((opportunity, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <ErrorIcon sx={{ color: '#ff9800', mr: 1.5, mt: 0.25 }} />
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', flex: 1 }}>
                          {opportunity}
                        </Typography>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Grid>

              {/* Passed Audits */}
              <Grid item xs={12} md={6}>
                <Card sx={{ 
                  background: 'linear-gradient(135deg, rgba(26, 31, 61, 0.8) 0%, rgba(10, 17, 40, 0.9) 100%)',
                  border: '1px solid rgba(139, 0, 0, 0.3)',
                  height: '100%',
                }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom sx={{ color: '#ff4d4d', mb: 3 }}>
                      Passed Audits
                    </Typography>
                    
                    {results.passedAudits.map((audit, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <CheckCircleIcon sx={{ color: '#4caf50', mr: 1.5, mt: 0.25 }} />
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', flex: 1 }}>
                          {audit}
                        </Typography>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4, flexWrap: 'wrap' }}>
              <Button 
                variant="contained" 
                size="large"
                onClick={generatePDF}
                startIcon={<Download/>}
              >
                Download Report (TXT)
              </Button>
              <Button 
                variant="outlined" 
                size="large" 
                onClick={() => {
                  setResults(null);
                  setUrl('');
                }}
              >
                Analyze Another Website
              </Button>
            </Box>

            {/* Download Success Message */}
            <Alert 
              severity="success" 
              sx={{ 
                mt: 2, 
                background: 'rgba(76, 175, 80, 0.1)',
                border: '1px solid rgba(76, 175, 80, 0.3)',
                color: 'white',
                display: 'none', // Hidden by default, can be shown on download
              }}
              id="downloadSuccess"
            >
              Report downloaded successfully!
            </Alert>
          </Container>
        </Section>
      )}
    </>
  );
};

export default PerformanceAnalyzer;